import React, { useContext, useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import Smartlook from 'smartlook-client';

import { noop } from 'lodash';

import { firestore } from '@isd/lib/firebase';
import {
  checkAuth,
  loginWithCustomToken,
  onAuthStateChanged,
  signOut,
} from '@isd/lib/firebase/auth';
import { Companies } from '@isd/lib/firebase/db/collections';
import {
  getUserCompanyId,
  loadUserDocument,
} from '@isd/lib/firebase/db/helpers';
import { CompanyDoc, UserDoc } from '@isd/lib/firebase/db/metaTypes';

import log from '@isd/lib/logger';

import SpinnerCircle from '@/components/Spinner/SpinnerCircle';

import { reactAppLoginRoute, reactAppUrl } from '@/env';

interface AuthContextType {
  userDoc: UserDoc | null;
  userCompanyDoc: CompanyDoc | null;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  userCompanyId: string;
}

export const AuthContext = React.createContext<AuthContextType>({
  userDoc: null,
  userCompanyDoc: null,
  isAuthenticated: false,
  isImpersonating: false,
  userCompanyId: '',
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [userDoc, setUserDoc] = useState<UserDoc | null>(null);
  const [userCompanyDoc, setUserCompanyDoc] = useState<CompanyDoc | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [userCompanyId, setUserCompanyId] = useState('');
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);
  const searchParams = useSearchParams();

  useEffect(() => {
    let unsubscribeDoc = noop;
    const unsubscribeAuth = onAuthStateChanged(async (authUser) => {
      log.info('onAuthStateChanged - User:', authUser);
      setIsAuthenticated(!!authUser);

      // Check if session cookie is set
      let customToken = searchParams.get('customToken') || '';
      if (!customToken) {
        customToken = await checkAuth();
      }

      // When user is not logged in check if it can be authenticated with
      // session cookies or should be redirected to external login page
      if (!authUser && !initialized.current) {
        if (!!customToken) {
          // Use custom token to login
          await loginWithCustomToken(customToken);
        }
        // Check if redirect has been disabled by query params
        else if (searchParams.get('avoidRedirect') === null) {
          log.info('No token received, should redirect to external site');
          return redirectToLogin();
        }
      } else if (!!authUser) {
        // Obtain User ID from custom token and check it matches authenticated user uid
        const customTokenPayload = customToken.split('.')[1];
        const customTokenUid: string = JSON.parse(
          !!customTokenPayload
            ? atob(customTokenPayload) // Token payload is encoded in Base64
            : '{}'
        ).uid;
        const isWrongOrOutdatedToken = customTokenUid !== authUser.uid;

        // Check if session cookie is still valid.
        if (!customToken) {
          log.info(
            `Check auth didn't retrieve a new token, which means cookies were cleared, now should logout and redirect`
          );
          signOut().then(redirectToLogin);
        } else if (isWrongOrOutdatedToken) {
          // When token is for a different user (e.g. User A logged out and logged in as User B in the same browser),
          // logout and re log in using the custom token
          log.info(
            `Custom Token UID doesn't match logged user UID. So it should re-log in with new credentials`
          );
          await signOut().then(() => loginWithCustomToken(customToken));
        } else {
          // Merge user data from db into user object
          unsubscribeDoc = loadUserDocument(authUser, (userDocSnapshot) => {
            setUserDoc(userDocSnapshot || null);
            setUserCompanyId(getUserCompanyId(userDocSnapshot) || '');
            setIsImpersonating(authUser.uid !== userDocSnapshot.id);
            // Check if Smartlook was initialized on app load and identify current user
            if (Smartlook.initialized()) {
              Smartlook.identify(userDocSnapshot.id, {
                email: userDocSnapshot.get('email') || '',
                firstName: userDocSnapshot.get('firstName') || '',
                lastName: userDocSnapshot.get('lastName') || '',
                nickname: userDocSnapshot.get('nickname') || '',
              });
            }
          });
        }
      }

      setLoading(false);
      initialized.current = true;
    });

    return () => {
      unsubscribeAuth();
      unsubscribeDoc();
    };
  }, []);

  useEffect(() => {
    if (!userCompanyId) return;
    const unsubscribe = firestore.onSnapshot(
      Companies.doc(userCompanyId),
      (companyDoc) => setUserCompanyDoc(companyDoc)
    );
    return unsubscribe;
  }, [userCompanyId]);

  return (
    <AuthContext.Provider
      value={{
        userDoc,
        userCompanyDoc,
        isAuthenticated,
        userCompanyId,
        isImpersonating,
      }}
    >
      {loading ? <FullScreenSpinner /> : isAuthenticated ? children : null}
    </AuthContext.Provider>
  );
};

/**
 * Sends the user to the legacy app's login page, coming back to the current
 * URL once signed in.
 *
 * `reactAppUrl` is already same-origin-relative (empty) when this app is
 * served as a microfrontend — see `@/env` — so this stays same-origin
 * automatically, preserving the shared session cookie.
 *
 * @returns {void}
 */
function redirectToLogin() {
  // Set current url as query param to be redirected back once signed in
  const redirectToURL = window.location.href;
  const loginURL = `${reactAppUrl}${reactAppLoginRoute}?redirectTo=${encodeURIComponent(redirectToURL)}`;
  window.location.href = loginURL;
  return;
}

function FullScreenSpinner() {
  return (
    <div className="h-screen">
      <div className="flex h-full justify-center">
        <SpinnerCircle />
      </div>
    </div>
  );
}
